import { useState, useEffect, memo } from 'react';
import { Link } from 'react-router-dom';
import { Download, Trophy, TrendingUp, ExternalLink, Database } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { apogeeApiService } from '../../services/apogeeApi';
import { serverListApiService } from '../../services/serverListApi';
import { PackRow } from '../../types/apogee';
import { SkeletonLoader } from '../SkeletonLoader';
import { cn } from '../../utils/cn';
import { packDetailPath } from '../../utils/packRoutes';

interface ApogeePackEnrichmentProps {
  /** Pack name to search for — used for server-list MPI ID lookup (optional if `apogeeId` is set) */
  packName?: string;
  /** When set, load via `GET /v1/packs/id/{apogee_id}` (most reliable) */
  apogeeId?: number;
  /** Optional: if the MPI ID is already known, skip name-matching entirely */
  mpiId?: number;
  /** Optional: FQDN of a server that runs this pack — used for server-list lookup */
  serverFqdn?: string;
  /** Display mode: 'inline' for a compact bar, 'card' for a full card */
  mode?: 'inline' | 'card';
  className?: string;
}

const formatNumber = (n: number | undefined | null): string => {
  if (n == null || Number.isNaN(n)) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
};

export const ApogeePackEnrichment = memo(({
  packName = '',
  apogeeId,
  mpiId,
  serverFqdn,
  mode = 'inline',
  className,
}: ApogeePackEnrichmentProps) => {
  const [pack, setPack] = useState<PackRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setPack(null);

    if (apogeeId == null && !mpiId && !serverFqdn && !packName.trim()) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    const fetchPack = async () => {
      try {
        if (apogeeId != null) {
          const data = await apogeeApiService.getPackByApogeeId(apogeeId);
          setPack(data);
          return;
        }

        // Strategy 1: Direct MPI ID lookup (fastest, most reliable)
        if (mpiId) {
          const data = await apogeeApiService.getPackByMpiId(mpiId);
          setPack(data);
          return;
        }

        // Strategy 2: Resolve MPI ID from server FQDN via server-list API
        if (serverFqdn) {
          const resolvedId = await serverListApiService.getMpiIdForFqdn(serverFqdn);
          if (resolvedId) {
            const data = await apogeeApiService.getPackByMpiId(resolvedId);
            setPack(data);
            return;
          }
        }

        // Strategy 3: Search server-list by pack name to find MPI ID
        const serverList = await serverListApiService.getServerList();
        const normalizedName = packName.toLowerCase().trim();
        const matchedServer = serverList.find(
          (s) => s.modpackTitle?.toLowerCase() === normalizedName,
        );
        if (matchedServer?.modpackIndexId) {
          const data = await apogeeApiService.getPackByMpiId(matchedServer.modpackIndexId);
          setPack(data);
          return;
        }

        // Strategy 4: Search Apogee packs list directly by sorting and matching slug
        const slug = normalizedName.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        // Try fetching by a few common sort orders to find the pack
        const res = await apogeeApiService.listPacks({ limit: 200, sort: 'name' });
        const items = res.items || [];
        const match = items.find(
          (p) => p.name.toLowerCase() === normalizedName || p.slug === slug,
        );
        if (match) {
          setPack(match);
          return;
        }

        setNotFound(true);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPack();
  }, [packName, apogeeId, mpiId, serverFqdn]);

  if (loading) {
    return mode === 'inline' ? (
      <div className={cn('flex items-center gap-2', className)}>
        <SkeletonLoader className="h-7 w-40 rounded-lg" />
      </div>
    ) : (
      <div className={cn('glass-card p-4', className)} aria-busy="true">
        <SkeletonLoader className="h-4 w-32 mb-3" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonLoader key={i} className="h-10 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (notFound || !pack) return null;

  if (mode === 'inline') {
    return (
      <Link
        to={packDetailPath(pack)}
        className={cn(
          'inline-flex items-center gap-3 px-3 py-1.5 rounded-lg tabular-nums',
          'bg-accent-500/10 border border-accent-500/20 hover:border-accent-400/40 transition-colors',
          className,
        )}
        title="Open in modpack browser"
      >
        <div className="flex items-center gap-1 text-xs">
          <Database className="w-3 h-3 text-accent-300" />
          <span className="text-accent-300 font-medium">Apogee</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-glass-300">
          <Download className="w-3 h-3" />
          <span>{formatNumber(pack.download_count)}</span>
        </div>
        {typeof pack.popularity_rank === 'number' && (
          <div className="flex items-center gap-1 text-xs text-glass-400">
            <Trophy className="w-3 h-3" />
            <span>#{pack.popularity_rank}</span>
          </div>
        )}
        <ExternalLink className="w-3 h-3 text-glass-500" />
      </Link>
    );
  }

  // Card mode
  return (
    <div className={cn('glass-card p-4 space-y-3', className)}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Database className="w-4 h-4 text-accent-300 shrink-0" />
          <span className="text-sm font-semibold text-white truncate">Apogee Data</span>
        </div>
        <Link
          to={packDetailPath(pack)}
          className="glass-button-sm"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          View in Browser
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 tabular-nums">
        <div>
          <p className="eyebrow">Downloads</p>
          <p className="text-lg font-semibold tracking-tight text-white">{formatNumber(pack.download_count)}</p>
        </div>
        {typeof pack.popularity_rank === 'number' && (
          <div>
            <p className="eyebrow">Rank</p>
            <p className="text-lg font-semibold tracking-tight text-white">#{pack.popularity_rank}</p>
          </div>
        )}
        <div>
          <p className="eyebrow">First Seen</p>
          <p className="text-sm text-glass-300">
            {formatDistanceToNow(new Date(pack.first_seen_at), { addSuffix: true })}
          </p>
        </div>
        {pack.last_updated && (
          <div>
            <p className="eyebrow">Last Updated</p>
            <p className="text-sm text-glass-300">
              {formatDistanceToNow(new Date(pack.last_updated), { addSuffix: true })}
            </p>
          </div>
        )}
      </div>

      {pack.download_count != null &&
        pack.first_seen_download_count > 0 &&
        pack.download_count > pack.first_seen_download_count && (
        <div className="flex items-center gap-2 text-xs tabular-nums">
          <TrendingUp className="w-3.5 h-3.5 text-green-400" />
          <span className="text-green-300">
            +{formatNumber(pack.download_count - pack.first_seen_download_count)} downloads since tracking began
          </span>
        </div>
      )}

      <p className="text-xs text-glass-500 tabular-nums">
        {pack.mpi_id != null && (
          <>MPI ID: <span className="font-mono">{pack.mpi_id}</span> · </>
        )}
        Apogee ID: <span className="font-mono">{pack.apogee_id}</span>
        {pack.cf_mod_id != null && (
          <> · CF mod: <span className="font-mono">{pack.cf_mod_id}</span></>
        )}
      </p>
    </div>
  );
});

ApogeePackEnrichment.displayName = 'ApogeePackEnrichment';
